import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import styles from "./ContactUs.module.css";
import image4 from "../../svgs/Neuro-ExchangeDark.svg";
import { useLocale } from './LocaleContext';
import interactive from './AboutInteractive.module.css';
import { motion, AnimatePresence } from 'framer-motion';
import usePrefersReducedMotion from './usePrefersReducedMotion';

const ContactUs = () => {
  const { locale } = useLocale();
  const reduce = usePrefersReducedMotion();
  const t = translations[locale] || translations['en-US'];
  const [formData, setFormData] = useState({
    company: "",
    fullname: "",
    email: "",
    phone: "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);

    if (!formData.company || !formData.fullname || !formData.email) {
      setError(t.errors.required);
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t.errors.invalidEmail);
      setLoading(false);
      return;
    }

    const serviceID = process.env.REACT_APP_SERVICE_ID;
    const templateID = process.env.REACT_APP_CONTACT_TEMPLATE_ID;
    const userID = process.env.REACT_APP_USER_ID;
    const user_email = process.env.REACT_APP_CONTACT_USER_EMAIL;

  const templateParams = {
    company: formData.company,
    fullname: formData.fullname,
    email: formData.email,
    phone: formData.phone,
    user_email : user_email,
  };


    emailjs
      .send(serviceID, templateID, templateParams, userID)
      .then((response) => {
        setLoading(false);
        setMessage(t.success);
        setFormData({
          company: "",
          fullname: "",
          email: "",
          phone: "",
        });
      })
      .catch((error) => {
        setLoading(false);
        console.error(error); 
        setError(t.errors.sendFailed);
      });
  };

  return (
    <div className={styles.contactContainer}>
      <div className={styles.contactInfo}>
        <img className={styles.logo} src={image4} alt="Neuro Exchange Logo" />
        {locale === 'pt-BR' ? (
          <>
            <p>contact@neuro-exchange.com</p>
            <p>+55 11 99999-0000</p>
            <p>Av. Faria Lima, São Paulo (Representação)</p>
            <p>Brasil</p>
          </>
        ) : (
          <>
            <p>contact@trustanchorgroup.com</p>
            <p>+46 40 410 5890</p>
            <p>Blekholmsterrassen 36, 111 64, Stockholm</p>
            <p>Sweden</p>
          </>
        )}
        <button type="button" className={interactive.copyEmailBtn} onClick={()=>{
          navigator.clipboard.writeText(locale==='pt-BR'? 'contact@neuro-exchange.com':'contact@trustanchorgroup.com');
          setMessage(locale==='pt-BR'? 'E-mail copiado!' : 'Email copied!');
        }}>{locale==='pt-BR'? 'Copiar e-mail':'Copy email'}</button>
        <motion.div
          initial={reduce?false:{ opacity:0, y:26 }}
          whileInView={reduce?{}:{ opacity:1, y:0 }}
          viewport={{ once:true }}
          transition={{ duration:.75 }}
          style={{ marginTop:'1.2rem', padding:'.85rem 1rem', borderRadius:16, background:'linear-gradient(155deg, rgba(22,40,34,.55), rgba(14,20,22,.75))', border:'1px solid rgba(70,150,120,.35)', fontSize:'0.65rem', lineHeight:1.35 }}
          aria-label={locale==='pt-BR'? 'Painel de garantia e conformidade' : 'Assurance & compliance panel'}
        >
          <strong style={{ display:'block', fontSize:'0.66rem', letterSpacing:'1px', marginBottom:'.4rem' }}>{t.assurance?.title}</strong>
          <ul style={{ listStyle:'none', padding:0, margin:0, display:'grid', gap:'.35rem' }}>
            {t.assurance?.items.map(i=> <li key={i} style={{ opacity:.85 }}>{i}</li>)}
          </ul>
        </motion.div>
      </div>
      <motion.div className={styles.contactForm} initial={reduce?false:{ opacity:0, y:40 }} whileInView={reduce?{}:{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:.7 }}>
        <h1 style={{
          fontSize:'clamp(1.7rem,4.6vw,2.6rem)',
          letterSpacing:'.8px',
          color:'#F5C84C',
          fontWeight:700,
          textAlign:'center',
          textShadow:'0 2px 16px #0C0D0E88,0 1.5px 0 #14B88D',
          margin:'0 0 12px',
          fontFamily:'Space Grotesk',
        }}>{t.heading}</h1>
        <p style={{
          fontSize:'clamp(.9rem,1.6vw,1.15rem)',
          lineHeight:1.4,
          color:'#14B88D',
          fontWeight:500,
          textAlign:'center',
          margin:'0 0 22px',
          fontFamily:'Space Grotesk',
        }}>{t.subheading}</p>
        <motion.div
          initial={reduce?false:{ opacity:0, y:18 }}
          whileInView={reduce?{}:{ opacity:1, y:0 }}
          viewport={{ once:true }}
          transition={{ duration:.6 }}
          style={{
            padding:'0.75rem 1rem',
            border:'1px solid #F5C84C88',
            borderRadius:14,
            background:'rgba(20,34,28,.55)',
            fontSize:'0.95rem',
            marginBottom:'1rem',
            color:'#BFEFDD',
            textAlign:'center',
          }}
        >
          {t.narrative}
        </motion.div>
        <form onSubmit={handleSubmit} aria-live="polite">
          <div className={styles.formGroup}>
            <input
              type="text"
              name="company"
              placeholder={t.fields.company}
              value={formData.company}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="fullname"
              placeholder={t.fields.fullname}
              value={formData.fullname}
              onChange={handleChange}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <input
              type="email"
              name="email"
              placeholder={t.fields.email}
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <input
              type="tel"
              name="phone"
              placeholder={locale === 'pt-BR' ? '+55 (DDD) Número' : t.fields.phone}
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
          <motion.button whileHover={reduce?{}:{ scale:1.05 }} whileTap={reduce?{}:{ scale:0.94 }} className={styles.formGroupButton} type="submit" disabled={loading}>
            {loading ? t.loading : t.cta}
          </motion.button>
        </form>
        <AnimatePresence>
          {error && (
            <motion.div className={styles.errorMessage} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }}>
              {error}
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {message && (
            <motion.div className={`${styles.successMessage} ${interactive.successCheck}`} initial={{ opacity:0, scale:.9 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0, scale:.9 }}>
              <span>✓</span> {message}
            </motion.div>
          )}
        </AnimatePresence>
  <div className={styles.disclaimer} style={{ color:'#F5C84C', textAlign:'center', fontWeight:500, marginTop:'1.2rem' }}>{t.disclaimer}</div>
  {t.privacy && <div style={{ fontSize:'0.58rem', marginTop:'0.6rem', opacity:.55, color:'#BFEFDD', textAlign:'center' }}>{t.privacy}</div>}
      </motion.div>
    </div>
  );
};

export default ContactUs;

const translations = {
  'en-US': {
    heading: 'Contact us',
    subheading: "We'd love to talk!",
    cta: 'Send request',
    loading: 'Sending...',
    success: 'Thank you for contacting us! We will get back to you soon.',
    disclaimer: 'Your submission is non-binding and not an offer to purchase securities. We may follow up with informational material only.',
    narrative: 'Purpose → Proof: Each partnership inquiry is evaluated through compliance, identity governance & impact alignment checkpoints before activation.',
    assurance: { title:'Assurance', items:['Audited Neuro-Contracts','Continuous KYC / AML','LGPD Data Minimization','CVM Framework Alignment','Hash-anchored lifecycle events'] },
    privacy: 'Privacy: Minimal personal data collected; used solely for follow-up. Subject to LGPD & applicable international standards.',
    fields: { company: 'Company name', fullname: 'Full name', email: 'Work email', phone: 'Phone number' },
    errors: { required: 'Please fill in all required fields.', invalidEmail: 'Please enter a valid email address.', sendFailed: 'Failed to send the message. Please try again later.' }
  },
  'pt-BR': {
    heading: 'Fale conosco',
    subheading: 'Adoramos conversar!',
    cta: 'Enviar pedido',
    loading: 'Enviando...',
    success: 'Obrigado pelo contato! Retornaremos em breve.',
    disclaimer: 'Sua solicitação não é vinculante e não constitui oferta de compra de valores mobiliários. Podemos enviar materiais apenas informativos.',
    narrative: 'Propósito → Prova: Cada solicitação é avaliada por conformidade, governança de identidade e alinhamento de impacto antes da ativação.',
    assurance: { title:'Garantia', items:['Neuro-Contracts Auditados','KYC / AML Contínuo','Minimização de Dados (LGPD)','Alinhamento a Frameworks CVM','Eventos com hash on-chain'] },
    privacy: 'Privacidade: Coletamos dados mínimos para retorno. Uso exclusivo para follow-up. Sujeito à LGPD.',
    fields: { company: 'Nome da empresa', fullname: 'Nome completo', email: 'E-mail corporativo', phone: 'Telefone' },
    errors: { required: 'Preencha todos os campos obrigatórios.', invalidEmail: 'Digite um e-mail válido.', sendFailed: 'Falha ao enviar a mensagem. Tente novamente mais tarde.' }
  }
};

